import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input } from "reactstrap";
import { Edge, Node } from "reactflow";
import { SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";

import { FlowChartConfigI, View } from "../../../types/PageInterfaces";
import { modulesAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useRecord from "../../../api/api-hooks/records/useRecord";
import { FileInterface } from "../../../types/RecordInterfaces";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import AppEmptyState from "../../../elements/AppEmptyState";
import RequiredTitle from "../../../elements/RequiredTitle";
import AppFieldCard from "../../../elements/AppFieldCard";
import { Module } from "../../../types/TenantInterfaces";
import { newToastEvent } from "../../../utils/ToastUtil";
import { Page } from "../../../types/GlobalInterfaces";
import AppSpinner from "../../../elements/AppSpinner";
import TenantAPI from "../../../api/api/TenantAPI";
import useModule from "../../../hooks/useModule";
import FlowChartConfig from "./FlowChartConfig";
import AppDrawer from "../../Drawers/AppDrawer";
import PageAPI from "../../../api/api/PageAPI";
import { RecordNode } from "./AppFlowChart";

export const FlowTypes = [
  {
    value: "Custom",
    label: "Custom Flow Chart",
    description:
      "Create your own flow chart, with records, images, and more as nodes.",
    icon: "mdi mdi-sitemap",
  },
  {
    value: "Hierarchy",
    label: "Hierarchical Flow Chart",
    description:
      "Automatically creates a hierarchy of nodes based on your data.",
    icon: "mdi mdi-sitemap-outline",
  },
];

type FlowType = "Custom" | "Hierarchy";

export const initialFlowChart = () => ({ Modules: {} });

export interface FlowChart {
  Name: string;
  Error: string | boolean | undefined;
  Description: string;
  ViewID: string;
  FlowChartConfig: FlowChartConfigI;
  FlowChartNodes: Node<RecordNode>[];
  FlowChartEdges: Edge[];
  FlowType: FlowType;
  TopLevelRecordID: string | undefined;
  Attachments: FileInterface[];
}

const FlowChartDrawer = ({
  open,
  onClose,
  flowChartID,
  flowChart,
  view,
  page,
  recordLoading,
}: {
  open: boolean;
  onClose: () => void;
  flowChartID: string;
  flowChart?: FlowChart;
  view: View;
  page: Page;
  recordLoading: boolean;
}) => {
  const [flowData, setFlowData] = useState<Partial<FlowChart>>({
    Name: "",
    Description: "",
    FlowChartConfig: initialFlowChart(),
    FlowChartNodes: [],
    FlowChartEdges: [],
    TopLevelRecordID: undefined,
    Attachments: [],
  });
  const [flowType, setFlowType] = useState(
    flowChart ? flowChart?.FlowType ?? "Custom" : null
  );

  const {
    upsertFlow,
    submitting,
    error,
    clearError,
    topLevelRecords,
    clearTopLevelRecords,
  } = useUpsertFlow({
    view,
    pageID: page.id!,
  });

  useEffect(() => {
    if (!flowChart) return;

    setFlowType(flowChart?.FlowType ?? "Custom");
    setFlowData({
      Name: flowChart?.Name,
      Description: flowChart?.Description,
      FlowChartConfig: flowChart?.FlowChartConfig,
      FlowChartNodes: flowChart?.FlowChartNodes,
      FlowChartEdges: flowChart?.FlowChartEdges,
      TopLevelRecordID: flowChart?.TopLevelRecordID,
      Attachments: flowChart?.Attachments,
    });
  }, [flowChart]);

  return (
    <>
      <AppDrawer
        open={open}
        error={error}
        onClose={handleOnClose}
        headerTitle={flowChart ? "Update Flow" : "New Flow"}
        headerDescription={
          flowType
            ? `${flowType === "Custom" ? flowType : "Hierarchical"} Flow Chart`
            : undefined
        }
        headerOptions={
          <>
            {flowType && (
              <>
                {!flowChart && (
                  <Button
                    size="sm"
                    style={{ width: 80 }}
                    onClick={() => setFlowType(null)}
                  >
                    Back
                  </Button>
                )}
                <Button
                  color="primary"
                  size="sm"
                  style={{ width: 100 }}
                  onClick={() => {
                    // TODO: Add a warning here.
                    // TODO: Add a button to return to the previous step.
                    if (!flowType) return;

                    const payload: Partial<FlowChart> = {
                      Name: flowData.Name,
                      Description: flowData?.Description,
                      ViewID: view?.id,
                      FlowChartConfig: flowData.FlowChartConfig,
                      FlowType: flowType,
                      TopLevelRecordID: flowData.TopLevelRecordID,
                      Attachments: flowData.Attachments,
                    };

                    if (!flowChartID) {
                      payload.FlowChartNodes = [];
                      payload.FlowChartEdges = [];
                    }

                    upsertFlow({
                      flowID: flowChartID,
                      payload,
                      onSuccess: () => {
                        onClose();
                      },
                    });
                  }}
                  disabled={
                    !flowData?.FlowChartConfig ||
                    !Object.keys(flowData?.FlowChartConfig?.Modules)?.length ||
                    submitting ||
                    !flowData.Name ||
                    (flowType === "Hierarchy" &&
                      !Object.keys(flowData?.FlowChartConfig?.Modules).find(
                        (m) =>
                          !!flowData?.FlowChartConfig?.Modules[m].ParentField
                      ))
                  }
                >
                  {submitting ? (
                    <AppSpinner color="white" />
                  ) : flowChartID ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </>
            )}
          </>
        }
        body={(containerRef) => (
          <>
            {!flowType ? (
              <>
                {FlowTypes.map((flowTypeData) => {
                  return (
                    <AppFieldCard
                      full
                      key={flowTypeData.value}
                      // @ts-ignore
                      onClick={() => setFlowType(flowTypeData.value)}
                      isSelected={flowType === flowTypeData.value}
                      icon={flowTypeData.icon}
                      name={flowTypeData.label}
                      description={flowTypeData.description}
                      css="mb-2"
                      innerCSS=""
                      disabled={false}
                    />
                  );
                })}
              </>
            ) : (
              <>
                {flowChartID ? (
                  recordLoading ? (
                    <div className="h-100 w-100 d-flex justify-content-center align-items-center">
                      <AppSpinner size="xl" />
                    </div>
                  ) : flowChart ? (
                    <FlowBody
                      flowType={flowType}
                      flowData={flowData}
                      setFlowData={setFlowData}
                      isExisting={!!flowChart}
                      containerRef={containerRef}
                      topLevelRecords={topLevelRecords}
                    />
                  ) : (
                    <AppEmptyState
                      title="Flow Not Found"
                      description="This flow no longer exists."
                      hideIcon
                    />
                  )
                ) : (
                  <FlowBody
                    flowType={flowType}
                    flowData={flowData}
                    setFlowData={setFlowData}
                    isExisting={!!flowChart}
                    containerRef={containerRef}
                    topLevelRecords={topLevelRecords}
                  />
                )}
              </>
            )}
          </>
        )}
      />
    </>
  );

  function handleOnClose() {
    onClose();
    setFlowType(null);
    clearError();
    clearTopLevelRecords();
    setFlowData({
      Name: "",
      Description: "",
      FlowChartConfig: initialFlowChart(),
      FlowChartNodes: [],
      FlowChartEdges: [],
      TopLevelRecordID: undefined,
    });
  }
};

function FlowBody({
  flowType,
  flowData,
  setFlowData,
  isExisting,
  containerRef,
  topLevelRecords,
}: {
  flowType: FlowType;
  flowData: Partial<FlowChart>;
  setFlowData: Dispatch<SetStateAction<Partial<FlowChart>>>;
  isExisting: boolean;
  containerRef: any;
  topLevelRecords: { id: string; _RecordName: string }[];
}) {
  return (
    <>
      {/** Basic flow chart body */}
      <RequiredTitle title="Name" size="md" />
      <Input
        bsSize="sm"
        placeholder="Flow Name"
        className="mb-4"
        value={flowData.Name ?? ""}
        onChange={(e) => setFlowData({ ...flowData, Name: e.target.value })}
      />
      <h4>Description</h4>
      <Input
        type="textarea"
        bsSize="sm"
        placeholder="Flow Description"
        className="mb-4"
        value={flowData.Description ?? ""}
        onChange={(e) =>
          setFlowData({ ...flowData, Description: e.target.value })
        }
      />
      {flowType === "Custom" ? (
        <CustomFlowChartBody
          flowData={flowData}
          setFlowData={setFlowData}
          containerRef={containerRef}
        />
      ) : (
        <HierarchicalFlowChartBody
          flowData={flowData}
          setFlowData={setFlowData}
          isExisting={isExisting}
          containerRef={containerRef}
          topLevelRecords={topLevelRecords}
        />
      )}
    </>
  );
}

export function CustomFlowChartBody({
  flowData,
  setFlowData,
  containerRef,
}: {
  flowData: Partial<FlowChart>;
  setFlowData: Dispatch<SetStateAction<Partial<FlowChart>>>;
  containerRef?: any;
}) {
  return (
    <>
      <FlowChartConfig
        flowChartConfig={flowData.FlowChartConfig ?? initialFlowChart()}
        setFlowChartConfig={(config) =>
          setFlowData({ ...flowData, FlowChartConfig: config })
        }
        containerRef={containerRef}
      />
    </>
  );
}

export function HierarchicalFlowChartBody({
  flowData,
  setFlowData,
  isExisting,
  moduleSelectionDisabled,
  containerRef,
  topLevelRecords,
}: {
  flowData: Partial<FlowChart>;
  setFlowData: Dispatch<SetStateAction<Partial<FlowChart>>>;
  isExisting: boolean;
  moduleSelectionDisabled?: boolean;
  containerRef?: any;
  topLevelRecords: { id: string; _RecordName: string }[];
}) {
  const modules = useAtomValue(modulesAtom);
  const { getModule } = useModule();
  const [module, setModule] = useState<Module | null>(null);

  useEffect(() => {
    if (!flowData.FlowChartConfig) return;

    const moduleAPINames = Object.keys(flowData.FlowChartConfig.Modules);

    setModule(getModule(moduleAPINames[0]));
  }, [flowData?.FlowChartConfig?.Modules]);

  const filteredModules = useMemo(() => {
    return modules?.filter((m) => !m.Default) || [];
  }, [modules]);

  const selectableFields = useMemo(() => {
    if (!module || !module?.Fields?.length) return [];

    return module?.Fields.filter((f: any) => f.FieldType === "SingleReference");
  }, [module]);

  return (
    <>
      <RequiredTitle title="Data Sources" size="md" className="mb--2" />
      <small>
        Pick data sources for the flow builder; their records can then be added
        as nodes
      </small>
      <SelectPicker
        container={containerRef ? () => containerRef.current : undefined}
        preventOverflow
        disabled={isExisting || moduleSelectionDisabled}
        data={filteredModules}
        style={{ width: "100%" }}
        placeholder="Select Data Source"
        block
        className="my-2"
        onChange={(m: any) => {
          setModule(getModule(m));
        }}
        labelKey="Name"
        valueKey="APIName"
        value={module?.APIName}
        onClean={() => {
          setModule(null);
          setFlowData({
            ...flowData,
            FlowChartConfig: {
              Modules: {},
            },
          });
        }}
      />
      {module && (
        <>
          <RequiredTitle
            title="Parent Field"
            size="md"
            className="mt-4"
            titleCSS="mb-0"
          />
          <div className="small mb-2">
            This field will define your flow chart's hierarchy
          </div>
          <SelectPicker
            container={containerRef ? () => containerRef.current : undefined}
            preventOverflow
            data={selectableFields}
            labelKey="Name"
            valueKey="APIName"
            block
            disabled={isExisting}
            placeholder="Select Parent Field"
            onChange={(val) =>
              setFlowData({
                ...flowData,
                FlowChartConfig: {
                  Modules: {
                    [module?.APIName]: {
                      Fields: [],
                      ParentField: val ?? undefined,
                    },
                  },
                },
              })
            }
            value={
              flowData?.FlowChartConfig?.Modules?.[module?.APIName]?.ParentField
            }
            onClean={() =>
              setFlowData({
                ...flowData,
                FlowChartConfig: {
                  Modules: {
                    [module?.APIName]: {
                      Fields: [],
                      ParentField: undefined,
                    },
                  },
                },
              })
            }
          />
          {topLevelRecords.length > 0 && (
            <>
              <RequiredTitle
                title="Top Level Record"
                size="md"
                className="mt-4"
                titleCSS="mb-0"
              />
              <div className="small mb-2">
                Select the record that will be the top level record in the flow
              </div>
              <SelectPicker
                searchable={topLevelRecords.length > 5}
                placeholder="Select Top Level Record"
                block
                data={topLevelRecords}
                labelKey="_RecordName"
                valueKey="id"
                value={flowData?.TopLevelRecordID}
                onChange={(val: any) => {
                  setFlowData((prev) => ({ ...prev, TopLevelRecordID: val }));
                }}
                onClean={() =>
                  setFlowData((prev) => ({
                    ...prev,
                    TopLevelRecordID: undefined,
                  }))
                }
              />
            </>
          )}
          <h4 className="mt-4 mb-0">Field To Display</h4>
          <div className="small mb-2">
            Select the field you want to display under the record's name
          </div>
          <SelectPicker
            container={containerRef ? () => containerRef.current : undefined}
            preventOverflow
            data={
              module?.Fields?.filter(
                (f) =>
                  ![
                    "Tags",
                    "Image",
                    "SingleFile",
                    "Audio",
                    "Video",
                    "Subitem",
                    "MultiSubitem",
                  ].includes(f.FieldType) && !["id"].includes(f.APIName)
              ) || []
            }
            labelKey="Name"
            valueKey="APIName"
            block
            placeholder="Select Field"
            onChange={(val) =>
              setFlowData({
                ...flowData,
                FlowChartConfig: {
                  Modules: {
                    [module?.APIName]: {
                      ...(flowData?.FlowChartConfig?.Modules?.[
                        module?.APIName
                      ] ?? {}),
                      Fields: [val as string],
                    },
                  },
                },
              })
            }
            value={
              flowData?.FlowChartConfig?.Modules?.[module?.APIName]?.Fields?.[0]
            }
            onClean={() =>
              setFlowData({
                ...flowData,
                FlowChartConfig: {
                  Modules: {
                    [module?.APIName]: {
                      ...(flowData?.FlowChartConfig?.Modules?.[
                        module?.APIName
                      ] ?? {}),
                      Fields: [],
                    },
                  },
                },
              })
            }
          />
          {isExisting && flowData?.TopLevelRecordID && (
            <TopLevelRecordInfo
              moduleAPIName={module?.APIName}
              recordID={flowData.TopLevelRecordID}
            />
          )}
          {isExisting && (
            <Alert color="warning">
              The Data Source and Parent Field cannot be changed once the flow
              has been created. We recommend you create a new flow instead.
            </Alert>
          )}
        </>
      )}
    </>
  );
}

export function useUpsertFlow({
  view,
  pageID,
}: {
  view: View;
  pageID: string;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [topLevelRecords, setTopLevelRecords] = useState<
    { id: string; _RecordName: string }[]
  >([]);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const client = useQueryClient();

  async function upsertFlow({
    payload,
    onSuccess,
    flowID,
  }: {
    payload: Partial<FlowChart>;
    onSuccess: () => void;
    flowID?: string;
  }) {
    setSubmitting(true);
    setError("");

    try {
      let promise;

      if (flowID) {
        promise = () => TenantAPI.modifyRecord("Flows", flowID, { payload });
      } else {
        if (payload.FlowType === "Hierarchy") {
          promise = () =>
            PageAPI.generateFlowChart(workspaceID, pageID, view?._id, payload);
        } else {
          promise = () => TenantAPI.insertRecord("Flows", payload);
        }
      }

      const data = await promise();
      const flowRecord = data.data.data.data;

      flowRecord &&
        client.setQueryData(
          [tenantID, "record", "Flows", flowRecord._id],
          flowRecord
        );

      if (!flowRecord) return; // TODO: error handling

      let viewFlows = view?.Flows || [];

      if (flowID) {
        viewFlows = viewFlows.map((f) => {
          if (f.RecordID !== flowID) return f;

          f.Name = payload.Name || "";
          f.Description = payload.Description || "";

          return f;
        });
      } else {
        viewFlows.push({
          Name: flowRecord.Name,
          Description: flowRecord.Description,
          RecordID: flowRecord._id,
        });
      }

      const viewData = await PageAPI.updateView(
        workspaceID,
        pageID,
        view?._id,
        {
          Flows: viewFlows,
        }
      );
      const updatedView = viewData.data.data.view;

      updatedView &&
        client.setQueryData(["view", updatedView?._id], updatedView);

      newToastEvent("success", "Successfully created flow.");
      onSuccess();
    } catch (err) {
      // @ts-expect-error
      setTopLevelRecords(err?.response?.data?.data?.topLevelRecords || []);

      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return {
    upsertFlow,
    submitting,
    error: error ? { message: error, onClear: () => setError("") } : null,
    clearError: () => setError(""),
    topLevelRecords,
    clearTopLevelRecords: () => setTopLevelRecords([]),
  };
}

function TopLevelRecordInfo({
  recordID,
  moduleAPIName,
}: {
  recordID: string;
  moduleAPIName: string;
}) {
  const { record, recordLoading } = useRecord({
    moduleAPIName: moduleAPIName,
    recordID: recordID,
  });

  if (!recordLoading && !record)
    return (
      <Alert color="danger mt-4">
        The selected top level record no longer exists. Please delete the flow
        and create a new one.
      </Alert>
    );

  return (
    <Alert className="py-2 mt-4">
      <strong>{record?._RecordName}</strong> was selected to be the top level
      record in the flow.
    </Alert>
  );
}

export default FlowChartDrawer;
